import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface UseSwipeToGoBackOptions {
  minSwipeDistance?: number;
  edgeThreshold?: number; // Distance from left edge where swipe should start
  enabled?: boolean;
}

interface UseSwipeToGoBackReturn {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
}

export const useSwipeToGoBack = ({
  minSwipeDistance = 50,
  edgeThreshold = 50, // Only trigger if swipe starts within 50px from left edge
  enabled = true,
}: UseSwipeToGoBackOptions = {}): UseSwipeToGoBackReturn => {
  const navigate = useNavigate();
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);
  const canGoBack = useRef(window.history.length > 1);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (!enabled) return;

    const touch = e.targetTouches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;

    // Only start tracking if the touch starts near the left edge
    if (startX <= edgeThreshold) {
      setTouchEnd(null);
      setTouchStart({ x: startX, y: startY });
    }
  }, [enabled, edgeThreshold]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!enabled || !touchStart) return;

    const touch = e.targetTouches[0];
    setTouchEnd({ x: touch.clientX, y: touch.clientY });
  }, [enabled, touchStart]);

  const onTouchEnd = useCallback(() => {
    if (!enabled || !touchStart || !touchEnd || !canGoBack.current) return;

    const horizontalDistance = touchEnd.x - touchStart.x;
    const verticalDistance = Math.abs(touchEnd.y - touchStart.y);

    // Check if it's a right swipe (positive horizontal distance)
    // and horizontal movement is greater than vertical (to avoid triggering on scrolls)
    const isRightSwipe = horizontalDistance > minSwipeDistance;
    const isHorizontalSwipe = horizontalDistance > verticalDistance;

    if (isRightSwipe && isHorizontalSwipe) {
      navigate(-1);
    }

    // Reset touch state
    setTouchStart(null);
    setTouchEnd(null);
  }, [enabled, touchStart, touchEnd, minSwipeDistance, navigate]);

  return { onTouchStart, onTouchMove, onTouchEnd };
};
